import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import type { PatientIdentity } from '@/features/patients/types';
import { api } from '@/lib/api';

const LIVE_MS = 30_000;

function useBranchKey() {
  const { selected, canSelectAll } = useBranch();
  return selected?.id ?? (canSelectAll ? 'all' : 'none');
}

/** Sidebar count badges, keyed by page id (only counts the role may see). */
export function useNavCounts(enabled = true) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['shell', 'counts', branch],
    queryFn: async ({ signal }) =>
      (await api.get<Record<string, number>>('/shell/counts', { signal })).data,
    enabled: enabled && branch !== 'none',
    refetchInterval: LIVE_MS,
  });
}

/**
 * Pages whose badge comes from other `/shell/counts` keys: the doctor's inbox
 * shows its tabs' waiting work (RL-1158 refills & outside pharmacy, RL-1139
 * questions from the app, RL-1255 reading alerts nobody has acknowledged).
 * Every other page uses its own id.
 */
const NAV_COUNT_KEYS: Record<string, string[]> = {
  inbox: [
    'prescriptionRequests',
    'patientQuestions',
    'readingAlerts',
    // RL-1640: "Notify doctor" notices not marked seen.
    'outOfStockNotices',
  ],
  // RL-1673: chats from the app waiting for a nurse.
  patientChat: ['clinicChats'],
};

/** The sidebar badge for a page (0 = none). */
export function navCount(
  counts: Record<string, number> | undefined,
  pageId: string,
): number {
  const keys = NAV_COUNT_KEYS[pageId] ?? [pageId];
  return keys.reduce((n, k) => n + (counts?.[k] ?? 0), 0);
}

export interface ReminderGroup {
  key: string;
  label: string;
  items: { id: string; title: string; detail: string; href: string }[];
}

export function useReminders(enabled = true) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['shell', 'reminders', branch],
    queryFn: async ({ signal }) =>
      (await api.get<ReminderGroup[]>('/shell/reminders', { signal })).data,
    enabled: enabled && branch !== 'none',
    refetchInterval: LIVE_MS,
  });
}

export interface SearchResults {
  patients:
    | {
        id: string;
        fullName: string;
        identity: PatientIdentity | null;
        href: string;
      }[]
    | null;
  labOrders:
    | {
        id: string;
        number: string | null;
        patientName: string;
        tests: string[];
        status: string;
        href: string;
      }[]
    | null;
  bills:
    | {
        id: string;
        number: string;
        patientName: string;
        status: string;
        totalSen: number;
        href: string;
      }[]
    | null;
}

export function useGlobalSearch(q: string) {
  const branch = useBranchKey();
  const term = q.trim();
  return useQuery({
    queryKey: ['shell', 'search', branch, term],
    queryFn: async ({ signal }) =>
      (await api.get<SearchResults>('/search', { signal, query: { q: term } }))
        .data,
    enabled: term.length >= 2 && branch !== 'none',
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}
